import PropTypes from 'prop-types';
//
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { datasetsActions, datasetsUtils } from 'app-components/flux/datasets';
import { isDevBuild } from 'app-components/utils/core';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SwitchDatasetIcon from '@mui/icons-material/KeyboardTab';
import ThreeDotIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import SupervisedUserIcon from '@mui/icons-material/SupervisedUserCircle';

import AddShareDialog from './AddShareDialog';
import ShowManageSharesDialog from './ManageSharesDialog';
import UpsertDatasetDialog from './UpsertDatasetDialog';

const OwnedDatasetMenu = (props) => {
  const { dataset, shareCounts, shares } = props;
  const dispatch = useDispatch();

  const [sharesUsed, sharesAllowed] = shareCounts;
  const sharesLeft = Math.max(sharesAllowed - sharesUsed, 0);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showAddShareDialog, setShowAddShareDialog] = useState(false);
  const [showManageSharesDialog, setShowManageSharesDialog] = useState(false);
  const [showUpsertDatasetDialog, setShowUpsertDatasetDialog] = useState(false);

  const activeDatasetId = useSelector((state) => authSelectors.getDatasetId(state));

  const hasActiveEntitlement = useMemo(() => datasetsUtils.hasActiveEntitlement(dataset), [dataset]);

  const handleOpenMenu = (event) => setMenuAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleInviteUser = () => {
    handleCloseMenu();
    setShowAddShareDialog(true);
  };

  const handleManageUsers = () => {
    handleCloseMenu();
    setShowManageSharesDialog(true);
  };

  const handleUpsertDataset = () => {
    handleCloseMenu();
    setShowUpsertDatasetDialog(true);
  };

  const handleSelectDataset = () => {
    handleCloseMenu();
    dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset, userChoice: true })));
  };

  const handleDeleteDataset = () => {
    handleCloseMenu();
    dispatch(
      datasetsActions.deleteDataset(
        { id: dataset.id },
        { undo: { userMessage: 'Space deleted', disableUndoAction: true } },
      ),
    );
  };

  return (
    <>
      {showAddShareDialog && (
        <AddShareDialog dataset={dataset} onClose={() => setShowAddShareDialog(false)} shareCounts={shareCounts} />
      )}
      {showManageSharesDialog && (
        <ShowManageSharesDialog dataset={dataset} onClose={() => setShowManageSharesDialog(false)} shares={shares} />
      )}
      {showUpsertDatasetDialog && (
        <UpsertDatasetDialog dataset={dataset} onClose={() => setShowUpsertDatasetDialog(false)} />
      )}
      <IconButton
        id={`dataset-menu-button-${dataset.id}`}
        onClick={handleOpenMenu}
        size='large'
        disabled={!hasActiveEntitlement}
      >
        <ThreeDotIcon />
      </IconButton>
      <Menu
        id={`dataset-menu-${dataset.id}`}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem id='switch-space-button' disabled={dataset.id === activeDatasetId} onClick={handleSelectDataset}>
          <ListItemIcon>
            <SwitchDatasetIcon fontSize='small' />
          </ListItemIcon>
          Switch to this space
        </MenuItem>
        <Divider />
        <MenuItem id='update-space-button' onClick={handleUpsertDataset}>
          <ListItemIcon>
            <EditIcon fontSize='small' />
          </ListItemIcon>
          Update this space
        </MenuItem>
        <Divider />
        <MenuItem id='invite-space-button' disabled={!sharesLeft} onClick={handleInviteUser}>
          <ListItemIcon>
            <ShareIcon fontSize='small' />
          </ListItemIcon>
          Invite member {!!sharesUsed && !!sharesLeft && `(${sharesLeft})`}
        </MenuItem>
        <MenuItem disabled={!shares || shares.length === 0} onClick={handleManageUsers} id='manage-space-button'>
          <ListItemIcon>
            <SupervisedUserIcon fontSize='small' />
          </ListItemIcon>
          Manage members
        </MenuItem>

        {isDevBuild() && <Divider />}
        {isDevBuild() && (
          <MenuItem id='delete-space-button' onClick={handleDeleteDataset}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize='small' />
            </ListItemIcon>
            Delete (<BuildOutlinedIcon fontSize='small' />
            &nbsp; dev only)
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default OwnedDatasetMenu;

OwnedDatasetMenu.propTypes = {
  dataset: PropTypes.object,
  shareCounts: PropTypes.arrayOf(PropTypes.number),
  shares: PropTypes.arrayOf(PropTypes.object),
};
